<script setup lang="ts">
import {
  onBeforeMount,
  onUnmounted,
  ref,
  useSlots,
  watch,
} from 'vue';
import OopsTemplate from '@/vue/templates/oops-template.vue';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {useQuasar} from 'quasar';
import ErrorBox from '@/vue/molecules/error-box.vue';
import {DISABLE_LOADING} from '@/ts/utils/consts';
import {useRouter} from 'vue-router';
import {pagesPath} from '@/ts/router/pages-path';
import {ErrorCodesDto} from '@/ts/types/dto/error-codes-dto';

const slots = useSlots();
const logger = useLogger();
const $q = useQuasar();
const router = useRouter();

// Track if this instance triggered the spinner (to avoid hiding spinner started by another component)
const didShowSpinner = ref(false);

const props = defineProps<{
  error?: string | null;
  loading: boolean;
  type: 'component' | 'page' | 'page-ignore-header' | 'skeleton' | 'modal';
  id: string;
}>();

// Watch for consent and license errors and redirect
watch(() => props.error, (error) => {
  if (error === ErrorCodesDto.CONSENT_REQUIRED || error === ErrorCodesDto.CONSENT_NEEDS_REAPPROVAL) {
    $q.loading.hide();
    void router.push(pagesPath.main.consentRequired);
  } else if (error === ErrorCodesDto.NO_ACTIVE_LICENSES) {
    $q.loading.hide();
    void router.push({path: pagesPath.account.subscription, query: {reason: ErrorCodesDto.NO_ACTIVE_LICENSES}});
  }
}, {immediate: true});

defineSlots<{
  loader?(): any;
  error?(): any;
  default(): any;
}>();

onBeforeMount(() => {
  logger.debug(`onBeforeMount ${props.id} ${props.loading}`)();
  if (props.loading && !DISABLE_LOADING && props.type !== 'skeleton') {
    $q.loading.show();
    didShowSpinner.value = true;
  }
});

// Ensure spinner is hidden when component unmounts (e.g., during redirects)
onUnmounted(() => {
  if (didShowSpinner.value && !DISABLE_LOADING && props.type !== 'skeleton') {
    $q.loading.hide();
  }
});


watch(() => props.loading, (isLoading) => {
  logger.debug('isLoading', isLoading, props.id)();
  if (!DISABLE_LOADING && props.type !== 'skeleton') {
    if (isLoading) {
      $q.loading.show();
      didShowSpinner.value = true;
    } else {
      $q.loading.hide();
      didShowSpinner.value = false;
    }
  }
});

</script>
<template>
  <slot v-if="error && slots.error" name="error"/>
  <oops-template
    v-else-if="error && (type === 'page' || type === 'page-ignore-header' || type === 'modal')"
    :ignore-header="type === 'page-ignore-header'"
    :variant="type === 'modal' ? 'modal' : 'page'"
    :text="$t('the_server_encountered')"
    error-type="crash"
  />
  <error-box v-else-if="error"/>
  <template v-else-if="loading">
    <slot v-if="slots.loader" name="loader"/>
    <q-skeleton v-else-if="type === 'skeleton'" class="load-err-skel"/>
  </template>
  <slot v-else name="default"/>
</template>
<style lang="scss" scoped>
.load-err-skel {
  border-radius: var(--card-border-radius);
  height: 400px;
}
</style>
